(() => {
  if (customElements.get('brush-head-quiz')) return;
  class BrushHeadQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.selected = new Map();
      this.index = -1;
      this.busy = false;
      this.items = [...this.querySelectorAll('[data-type]')].map(el => ({ ...JSON.parse(el.querySelector('[data-settings]').textContent), id: el.dataset.id, type: el.dataset.type, el }));
      this.questions = this.items.filter(x => x.type === 'question');
      this.options = this.items.filter(x => x.type === 'answer');
      this.results = this.items.filter(x => x.type === 'result');
      this.stage = this.querySelector('.bhq__stage');
      this.addEventListener('click', e => this.handleClick(e), { signal: this.abort.signal });
      this.validate();
      this.querySelector('[data-start]').disabled = !this.questions.length;
    }
    disconnectedCallback() { this.abort?.abort(); this.animation?.cancel(); this.initialized = false; }
    matches(item, state) { return !item.show_shape || item.show_shape === 'any' || item.show_shape === state.shape; }
    // Evaluate in question order. Only an earlier answer can reveal a later question.
    evaluate() {
      const state = { shape: this.dataset.defaultShape, firmness: this.dataset.defaultFirmness };
      let explicitShape = false;
      const visible = [];
      const contexts = new Map();
      for (const q of this.questions) {
        if (!this.matches(q, state)) { this.selected.delete(q.id); continue; }
        visible.push(q);
        contexts.set(q.id, { ...state });
        const choices = this.options.filter(a => a.question_key === q.key && this.matches(a, state) && !(state.shape === 'long' && a.firmness === 'medium'));
        const answer = choices.find(a => a.id === this.selected.get(q.id));
        if (!answer) { this.selected.delete(q.id); continue; }
        if (!explicitShape && answer.suggest_shape && answer.suggest_shape !== 'any') state.shape = answer.suggest_shape;
        if (answer.shape && answer.shape !== 'any') { state.shape = answer.shape; explicitShape = true; }
        if (answer.firmness && answer.firmness !== 'any') state.firmness = answer.firmness;
      }
      if (state.shape === 'long' && state.firmness === 'medium') state.firmness = 'soft';
      return { state, visible, contexts };
    }
    async transition(update, direction = 1) {
      if (this.busy) return;
      this.busy = true;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const height = this.stage.getBoundingClientRect().height;
      try {
        if (!reduced && this.stage.animate) {
          this.animation = this.stage.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: `translateY(${-10 * direction}px)` }], { duration: 150, easing: 'ease-in', fill: 'forwards' });
          await this.animation.finished;
        }
        update();
        const newHeight = this.stage.getBoundingClientRect().height;
        this.animation?.cancel();
        if (!reduced && this.stage.animate) {
          this.animation = this.stage.animate([{ height: `${height}px`, opacity: 0, transform: `translateY(${14 * direction}px)` }, { height: `${newHeight}px`, opacity: 1, transform: 'translateY(0)' }], { duration: 330, easing: 'cubic-bezier(.22,1,.36,1)' });
          await this.animation.finished;
        }
        this.stage.querySelector(this.querySelector('[data-result]').hidden ? '[data-question-title]' : '[data-result-content] [tabindex="-1"]')?.focus({ preventScroll: true });
      } catch (error) { if (error.name !== 'AbortError') console.error(error); }
      finally { this.busy = false; }
    }
    handleClick(e) {
      const button = e.target.closest('button');
      if (!button || this.busy) return;
      if (button.hasAttribute('data-start') || button.hasAttribute('data-restart')) {
        this.selected.clear(); this.transition(() => { this.index = 0; this.render(); });
      } else if (button.hasAttribute('data-answer')) {
        const q = this.evaluate().visible[this.index];
        this.selected.set(q.id, button.dataset.answer);
        this.evaluate();
        this.querySelectorAll('[data-answer]').forEach(el => el.setAttribute('aria-pressed', String(el === button)));
        this.querySelector('[data-next]').disabled = false;
      } else if (button.hasAttribute('data-next')) {
        const { visible } = this.evaluate();
        if (!this.selected.has(visible[this.index]?.id)) return;
        this.transition(() => { this.index++; this.render(); });
      } else if (button.hasAttribute('data-back')) {
        this.transition(() => { this.index--; this.render(); }, -1);
      }
    }
    render() {
      const { state, visible, contexts } = this.evaluate();
      const result = this.index >= visible.length;
      this.querySelector('[data-intro]').hidden = true;
      this.querySelector('[data-question]').hidden = result;
      this.querySelector('[data-result]').hidden = !result;
      this.querySelector('.bhq__navigation').hidden = result;
      this.querySelector('.bhq__progress').hidden = result;
      this.querySelector('[data-progress]').style.width = `${result ? 100 : (this.index + 1) / visible.length * 100}%`;
      const step = result ? this.querySelector('[data-result] > .bhq__eyebrow').textContent : `${this.querySelector('[data-step-label]').textContent} ${this.index + 1} / ${visible.length}`;
      this.querySelector('[data-step]').textContent = step;
      this.querySelector('[data-status]').textContent = step;
      if (result) {
        const match = this.results.find(r => r.shape === state.shape && r.firmness === state.firmness);
        const template = match?.el.querySelector('template') || this.querySelector('[data-empty]');
        this.querySelector('[data-result-content]').replaceChildren(template.content.cloneNode(true));
        return;
      }
      const q = visible[this.index];
      const context = contexts.get(q.id);
      const heading = this.querySelector('[data-question-title]');
      heading.id = `bhq-question-${q.id}`;
      heading.textContent = q.title;
      this.querySelector('[data-question-description]').textContent = q.description || '';
      const container = this.querySelector('[data-answers]');
      container.setAttribute('aria-labelledby', heading.id);
      container.replaceChildren();
      this.options.filter(a => a.question_key === q.key && this.matches(a, context) && !(context.shape === 'long' && a.firmness === 'medium')).forEach((a, i) => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'bhq__answer'; button.dataset.answer = a.id;
        button.style.setProperty('--bhq-order', i);
        button.setAttribute('aria-pressed', String(this.selected.get(q.id) === a.id));
        button.append(a.el.querySelector('template').content.cloneNode(true));
        const copy = document.createElement('span'); copy.className = 'bhq__answer-copy';
        const title = document.createElement('span'); title.textContent = a.title; copy.append(title);
        if (a.description) { const desc = document.createElement('small'); desc.textContent = a.description; copy.append(desc); }
        const check = document.createElement('span'); check.className = 'bhq__check'; check.setAttribute('aria-hidden', 'true');
        button.append(copy, check); container.append(button);
      });
      this.querySelector('[data-next]').disabled = !this.selected.has(q.id);
      this.querySelector('[data-back]').disabled = this.index === 0;
    }
    validate() {
      const errors = [], keys = new Set(), pairs = new Set();
      this.questions.forEach(q => {
        if (!q.key || keys.has(q.key)) errors.push('问题 ID 缺失或重复：' + (q.key || q.title));
        keys.add(q.key);
        if (!this.options.some(a => a.question_key === q.key)) errors.push('问题没有答案：' + q.key);
      });
      this.options.forEach(a => { if (!keys.has(a.question_key)) errors.push('答案关联的问题不存在：' + a.title); });
      this.results.forEach(r => { const key = `${r.shape}/${r.firmness}`; if (pairs.has(key)) errors.push('推荐重复：' + key); pairs.add(key); if (!r.product) errors.push('推荐尚未关联商品：' + r.title); });
      ['round/soft', 'round/medium', 'round/hard', 'long/soft', 'long/hard'].forEach(key => { if (!pairs.has(key)) errors.push('缺少推荐：' + key); });
      const editor = this.querySelector('[data-editor-errors]');
      if (editor) editor.textContent = errors.join('；');
    }
  }
  customElements.define('brush-head-quiz', BrushHeadQuiz);
})();
